"use client";

import React from "react";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Send, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-24 bg-primary/10 animate-pulse rounded-sm" />)}
      </div>;
  }
  const links = menuItems && menuItems.length > 0 ? menuItems : [{
    id: "home",
    label: "Home",
    href: "/"
  }, {
    id: "services",
    label: "Services",
    href: "/services"
  }, {
    id: "inspiration",
    label: "Inspiration",
    href: "/inspiration"
  }, {
    id: "about",
    label: "About",
    href: "/about"
  }, {
    id: "contact",
    label: "Contact",
    href: "/contact"
  }];
  return <nav className="flex flex-col gap-3">
      {links.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="w-fit text-base font-medium hover:text-[var(--ring)] hover:underline hover:decoration-[var(--ring)] hover:underline-offset-4 transition-all">
          {item.label}
        </Link>)}
    </nav>;
}
function NewsletterForm() {
  const {
    handleSubmit,
    control,
    isSubmitting,
    isSubmitted,
    successMessage
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="p-4 bg-primary/5 border border-primary/10 rounded-md text-sm text-primary">
        {successMessage}
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex w-full items-start gap-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1">
              <FormControl>
                <Input placeholder="Your email address" className="bg-background border-primary/20 focus-visible:ring-[var(--ring)] h-10 placeholder:text-primary/50" {...field} />
              </FormControl>
              <FormMessage className="text-xs mt-1" />
            </FormItem>} />
        <Button type="submit" size="icon" disabled={isSubmitting} className="bg-primary hover:bg-primary/90 text-white h-10 w-10 shrink-0 cursor-pointer">
          {isSubmitting ? <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="size-4" />}
          <span className="sr-only">Subscribe</span>
        </Button>
      </div>
      <p className="text-xs text-primary/70">
        Join our newsletter for exclusive travel inspiration.
      </p>
    </Form>;
}
export default function BrandFooter() {
  const currentYear = new Date().getFullYear();
  return <footer id="brand-footer" className="bg-secondary text-primary font-sans border-t border-primary/10">
      <div className="container mx-auto px-6 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          <div className="flex flex-col gap-6">
            <Link to="/HomePage#" className="block w-fit">
              <img src="https://humble-drake.10web.cloud/wp-content/uploads/2026/02/cropped-SayKen-Tours-Final-Logo-1-e1728386750833.png" alt="Unforgettable Memories of Israel, One Tour at a Time" className="h-7 w-auto object-contain" />
            </Link>
            <p className="text-base leading-relaxed max-w-xs">
              We are experts in travel that is individual, inspired & imaginative. 
              Crafting tailor-made experiences for the discerning traveler.
            </p>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-bold uppercase tracking-wider">Explore</h3>
            <MenuProvider menu_id="17" className="w-full">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-bold uppercase tracking-wider">Contact</h3>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3 group">
                <MapPin className="size-5 shrink-0 mt-1 text-[var(--ring)]" />
                <span className="text-base group-hover:text-primary/80 transition-colors">
                  Tel Aviv, Israel
                </span>
              </div>
              <Link to="tel:+97231234567" className="flex items-center gap-3 group w-fit">
                <Phone className="size-5 shrink-0 text-[var(--ring)]" />
                <span className="text-base font-medium group-hover:text-[var(--ring)] transition-colors">+972 54-560-4547</span>
              </Link>
              <Link to="mailto:info@milkandhoney.co.il" className="flex items-center gap-3 group w-fit">
                <Mail className="size-5 shrink-0 text-[var(--ring)]" />
                <span className="text-base font-medium group-hover:text-[var(--ring)] transition-colors">info@saykentours.com</span>
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-bold uppercase tracking-wider">Stay Connected</h3>
            
            <div className="flex items-center gap-4">
              <Link to="https://instagram.com" newTab className="p-2 rounded-full border border-primary/20 hover:border-[var(--ring)] hover:bg-[var(--ring)] hover:text-white transition-all duration-300" aria-label="Instagram">
                <Instagram className="size-5" />
              </Link>
              <Link to="https://linkedin.com" newTab className="p-2 rounded-full border border-primary/20 hover:border-[var(--ring)] hover:bg-[var(--ring)] hover:text-white transition-all duration-300" aria-label="LinkedIn">
                <Linkedin className="size-5" />
              </Link>
              <Link to="https://facebook.com" newTab className="p-2 rounded-full border border-primary/20 hover:border-[var(--ring)] hover:bg-[var(--ring)] hover:text-white transition-all duration-300" aria-label="Facebook">
                <Facebook className="size-5" />
              </Link>
            </div>

            <div className="mt-2">
              <FormProvider formId="newsletter-footer" sectionName="footer" submitText="Subscribe" successMessage="Thank you for subscribing!" defaultValues={{
              email: ""
            }} className="w-full">
                <NewsletterForm />
              </FormProvider>
            </div>
          </div>
        </div>

        <Separator className="my-12 bg-primary h-px" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm font-medium opacity-80">
          <p>© {currentYear} SayKen Tours, All Rights Reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="hover:text-[var(--ring)] transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-[var(--ring)] transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>

      {}
      <Link to="https://wa.me/972545604547" newTab className="fixed bottom-[25px] right-[25px] z-[999999] bg-[#f97a00] text-white p-4 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-300 flex items-center justify-center !h-[80px] !w-[80px] hover:scale-110" aria-label="Contact us on WhatsApp">
        <MessageCircle className="size-6 fill-current" />
      </Link>
    </footer>;
}